import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useForm, useWatch } from 'react-hook-form';
import { toast } from 'sonner';

import { Form } from '@components/form';
import { HandlersSelect } from '@components/HandlersSelect';
import { InputControlled } from '@components/input/controlled';
import { PageTitle } from '@components/PageTitle';
import { Anchor } from '@components/ui/anchor';
import { Button } from '@components/ui/button';
import { Separator } from '@components/ui/separator';
import { languageOptions } from '@constants/languages.constant';
import { queryKeys } from '@constants/queryKeys.constant';
import { EBusinessLineType } from '@enums/businessLine.enum';
import { EStaleTime } from '@enums/staleTime.enum';
import { handleRequestError } from '@helpers/handleRequestError.helper';
import { businessLineService } from '@services/businessLines/service';
import {
  createServiceRequestSchema,
  type CreateServiceRequestFormInput,
  type CreateServiceRequestPayload,
} from '@services/serviceRequests/dtos/create.dto';
import { serviceRequestService } from '@services/serviceRequests/service';
import type { CreateServiceRequestOutput } from '@services/serviceRequests/types/create.type';

import { AssistanceFields } from './@components/AssistanceFields';
import { InsuranceFields } from './@components/InsuranceFields';

export const Route = createFileRoute(
  '/_layout/(private)/service-requests/create',
)({
  component: ServiceRequestCreate,
});

function ServiceRequestCreate() {
  const navigate = Route.useNavigate();

  const { data: businessLinesOptions, isFetching: isBusinessLinesPending } =
    useQuery({
      queryKey: queryKeys.businessLines.select,
      queryFn: () => businessLineService.getAllToSelect(),
      staleTime: EStaleTime.OneDay,
    });

  const form = useForm<
    CreateServiceRequestFormInput,
    unknown,
    CreateServiceRequestPayload
  >({
    resolver: zodResolver(createServiceRequestSchema),
    mode: 'onBlur',
    shouldUnregister: true,
    defaultValues: {
      requester: {
        serviceAtHomeAddress: true,
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
      },
      description: '',
      address: {
        street: '',
        number: '',
        postcode: '',
        city: '',
      },
      automaticEmailsEnabled: true,
      businessLineId: '',
      caller: '',
      caseId: '',
      damageCause: '',
      externalId: '',
      polis: '',
      source: '',
      thirdPartyDamage: false,
    },
  });

  const businessLineId = useWatch({
    control: form.control,
    name: 'businessLineId',
  });
  const businessLine =
    businessLineId && businessLinesOptions
      ? businessLinesOptions.list.find(
          option => option.value === businessLineId,
        )!
      : undefined;
  const isInsurance = businessLine?.type === EBusinessLineType.Insurance;
  const isAssistance = businessLine?.type === EBusinessLineType.Assistance;

  const isSameAsServiceRequest = useWatch({
    control: form.control,
    name: 'requester.serviceAtHomeAddress',
  });

  const { isPending, mutate: createServiceRequest } = useMutation<
    CreateServiceRequestOutput,
    unknown,
    CreateServiceRequestPayload
  >({
    mutationFn: serviceRequestService.create,
    onError: handleRequestError,
    onSuccess: ({ id }) => {
      navigate({
        to: '/service-requests/$id',
        params: { id },
        replace: true,
      });
      toast.success('Success', {
        description: 'Service request created successfully.',
      });
    },
  });

  const handleSubmit = form.handleSubmit(values => {
    createServiceRequest({
      address: values.address,
      description: values.description,
      handlerId: values.handlerId,
      requester: {
        address: values.requester.serviceAtHomeAddress
          ? values.address
          : values.requester.address,
        serviceAtHomeAddress: values.requester.serviceAtHomeAddress,
        email: values.requester.email,
        firstName: values.requester.firstName,
        language: values.requester.language,
        lastName: values.requester.lastName,
        phone: values.requester.phone,
      },
      source: values.source,
      automaticEmailsEnabled: values.automaticEmailsEnabled,
      businessLineId: values.businessLineId,
      caller: values.caller,
      caseId: values.caseId,
      damageCause: values.damageCause,
      externalId: values.externalId,
      polis: values.polis,
      thirdPartyDamage: values.thirdPartyDamage,
    });
  });

  return (
    <Form.Provider {...form}>
      <PageTitle hasBackButton title="Create Service Request" />

      <form className="space-y-2" onSubmit={handleSubmit}>
        <InputControlled
          className="ml-auto"
          formItemProps={{ className: 'ml-auto w-fit' }}
          name="automaticEmailsEnabled"
          title="Enable automatic emails"
          variant="switch"
        />

        <InputControlled
          isLoading={isBusinessLinesPending}
          label="Business Line"
          name="businessLineId"
          options={businessLinesOptions?.list ?? []}
          rootProps={{
            onValueChange: value => {
              if (value !== businessLineId) {
                form.resetField('caseId');
                form.resetField('polis');
              }
            },
          }}
          variant="select"
        />

        <InputControlled
          label="Description"
          maxLength={1000}
          name="description"
          rows={8}
          showCount
          variant="textarea"
        />
        {isInsurance && <InsuranceFields />}
        {isAssistance && <AssistanceFields />}
        {!isAssistance && <InputControlled label="Source" name="source" />}
        <HandlersSelect />

        <div className="grid gap-2.5 sm:grid-cols-2">
          <InputControlled label="Street" name="address.street" />
          <InputControlled label="Number" name="address.number" />
          <InputControlled label="Postcode" name="address.postcode" />
          <InputControlled label="City" name="address.city" />
        </div>

        <Separator variant="form" />

        <div className="grid h-fit items-start gap-2.5 sm:grid-cols-3">
          <InputControlled label="First name" name="requester.firstName" />
          <InputControlled label="Last name" name="requester.lastName" />
          <InputControlled label="Email" name="requester.email" type="email" />
          <InputControlled
            label="Phone"
            name="requester.phone"
            variant="phone"
          />
          <InputControlled
            label="Language"
            name="requester.language"
            options={languageOptions}
            variant="select"
          />
        </div>

        <div className="mt-3 grid space-y-2">
          <InputControlled
            name="requester.serviceAtHomeAddress"
            title="Official address same as request address"
            variant="checkbox"
          />

          {!isSameAsServiceRequest && (
            <div className="grid gap-2.5 sm:grid-cols-2">
              <InputControlled label="Street" name="requester.address.street" />
              <InputControlled label="Number" name="requester.address.number" />
              <InputControlled
                label="Postcode"
                name="requester.address.postcode"
              />
              <InputControlled label="City" name="requester.address.city" />
            </div>
          )}
        </div>

        <div className="mt-10 flex justify-center gap-x-2">
          <Anchor
            className="min-w-40 uppercase"
            disabled={isPending}
            rounded="lg"
            size="lg"
            to=".."
            variant="destructive"
          >
            Cancel
          </Anchor>
          <Button
            className="min-w-52"
            isLoading={isPending}
            size="lg"
            type="submit"
          >
            Create
          </Button>
        </div>
      </form>
    </Form.Provider>
  );
}
